/**
 * 机构管理模块 - 完整的CRUD功能
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Table, Button, Modal, message, Form, Upload, Input, Space, Switch } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined, EyeOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { institutionService } from '../../../../../services/institutionService';
import { PAGINATION_CONFIG } from '../../components/PaginationConfig';
import { DictSelect } from '../../../../../components/common/DictSelect';
import { DICT_TYPES } from '../../../../../constants/dictTypes';
import { useDict } from '../../../../../hooks/useUnifiedDict';
import QueryArea, { QueryField } from '../../components/QueryArea';

const { TextArea } = Input;

// 状态开关样式和Upload图标提示
const statusSwitchStyle = `
  .status-switch.ant-switch-checked {
    background-color: #52c41a !important;
  }
  .status-switch:not(.ant-switch-checked) {
    background-color: #ff4d4f !important;
  }
  
  /* Upload图标提示文字 */
  .ant-upload-list-item-actions .anticon-eye {
    cursor: pointer;
  }
  .ant-upload-list-item-actions .anticon-delete {
    cursor: pointer;
  }
  
  /* 自定义图标提示 */
  .upload-preview-icon::before {
    content: "查看";
    position: absolute;
    background: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 12px;
    white-space: nowrap;
    z-index: 1000;
    opacity: 0;
    transition: opacity 0.3s;
    pointer-events: none;
  }
  
  .upload-preview-icon:hover::before {
    opacity: 1;
  }
  
  .upload-delete-icon::before {
    content: "删除";
    position: absolute;
    background: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 12px;
    white-space: nowrap;
    z-index: 1000;
    opacity: 0;
    transition: opacity 0.3s;
    pointer-events: none;
  }
  
  .upload-delete-icon:hover::before {
    opacity: 1;
  }
  
  /* 自定义Upload组件样式 */
  .custom-upload-item {
    position: relative;
    display: inline-block;
  }
  
  .upload-actions {
    position: absolute;
    top: 0;
    right: 0;
    display: flex;
    gap: 4px;
    opacity: 1;
  }
  
  .custom-upload-item:hover .upload-actions {
    opacity: 1;
  }
  
  .upload-action-btn {
    color: #1890ff;
    cursor: pointer;
    padding: 4px;
    border-radius: 2px;
    transition: color 0.3s;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    background-color: rgba(255, 255, 255, 0.95);
    border: 1px solid #d9d9d9;
  }
  
  .upload-action-btn:hover {
    color: #40a9ff;
  }
`;

interface Institution {
  id: number;
  name: string;
  type: string;
  address: string;
  contactPerson: string;
  contactPhone: string;
  contactEmail: string;
  description: string;
  website?: string;
  logo?: string;
  status: number;
  createTime: string;
}

const InstitutionManagement: React.FC = () => {
  const [dataSource, setDataSource] = useState<Institution[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchParams, setSearchParams] = useState({
    keyword: '',
    type: '',
    status: '',
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<Institution | null>(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [logoFileList, setLogoFileList] = useState<any[]>([]);

  // 使用数据字典
  // 只从缓存读取字典数据，不触发API调用
  const { getLabelByValue: getTypeLabel } = useDict(DICT_TYPES.INSTITUTION_TYPE);
  const { getLabelByValue: getStatusLabel } = useDict(DICT_TYPES.INSTITUTION_STATUS);

  // 查询字段配置
  const queryFields: QueryField[] = [
    {
      key: 'keyword',
      label: '机构名称',
      type: 'input',
      placeholder: '搜索机构名称...',
      width: 200,
    },
    {
      key: 'type',
      label: '机构类型',
      type: 'dict',
      dictType: DICT_TYPES.INSTITUTION_TYPE,
      placeholder: '选择机构类型',
      width: 150,
    },
    {
      key: 'contactPerson',
      label: '联系人',
      type: 'input',
      placeholder: '搜索联系人...',
      width: 150,
    },
  ];

  // 表格列定义
  const columns: ColumnsType<Institution> = [
    {
      title: '机构名称',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      ellipsis: true,
    },
    {
      title: '机构类型',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (value) => getTypeLabel(value) || value,
    },
    {
      title: '联系人',
      dataIndex: 'contactPerson',
      key: 'contactPerson',
      width: 100,
    },
    {
      title: '联系电话',
      dataIndex: 'contactPhone',
      key: 'contactPhone',
      width: 130,
    },
    {
      title: '操作',
      key: 'action',
      width: 160,
      align: 'center',
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button 
            type="primary" 
            size="small" 
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Button 
            danger 
            size="small" 
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  // 加载数据 - 使用useCallback优化，避免不必要的重新创建
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        current,
        size: pageSize,
        ...searchParams,
      };
      
      const response = await institutionService.getList(params);
      if (response.code === 200) {
        setDataSource(response.data.records || []);
        setTotal(response.data.total || 0);
      } else {
        message.error(response.message || '加载数据失败');
      }
    } catch (error) {
      console.error('加载数据失败:', error);
      message.error('加载数据失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, [current, pageSize, searchParams]);

  // 监听分页和搜索参数变化，自动加载数据
  useEffect(() => {
    loadData();
  }, [current, pageSize, searchParams, loadData]);

  // 搜索
  const handleSearch = () => {
    setCurrent(1);
  };

  // 重置
  const handleReset = () => {
    setSearchParams({ keyword: '', type: '', status: '' });
    setCurrent(1);
  };

  // 处理查询参数变化
  const handleSearchParamsChange = useCallback((values: Record<string, any>) => {
    setSearchParams({
      keyword: values.keyword || '',
      type: values.type || '',
      status: values.status || '',
    });
  }, []);

  // 新增
  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    setLogoFileList([]);
    setIsModalVisible(true);
  };

  // 编辑
  const handleEdit = (record: Institution) => {
    setEditingRecord(record);
    form.setFieldsValue(record);
    // 初始化Logo文件列表
    if (record.logo) {
      setLogoFileList([{
        uid: '-1',
        name: 'logo.png',
        status: 'done',
        url: record.logo
      }]);
    } else {
      setLogoFileList([]);
    }
    setIsModalVisible(true);
  };

  // 删除
  const handleDelete = (id: number) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这个机构吗？删除后无法恢复。',
      okText: '确定',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        setLoading(true);
        try {
          const response = await institutionService.delete(id);
          if (response.code === 200) {
            message.success('删除成功');
            loadData();
          } else {
            message.error(response.message || '删除失败');
          }
        } catch (error) {
          console.error('删除失败:', error);
          message.error('删除失败，请稍后重试');
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // 批量删除
  const handleBatchDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要删除的数据');
      return;
    }
    
    Modal.confirm({
      title: '确认批量删除',
      content: `确定要删除选中的 ${selectedRowKeys.length} 个机构吗？删除后无法恢复。`,
      okText: '确定',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        setLoading(true);
        try {
          const response = await institutionService.batchDelete(selectedRowKeys as number[]);
          if (response.code === 200) {
            message.success('批量删除成功');
            setSelectedRowKeys([]);
            loadData();
          } else {
            message.error(response.message || '批量删除失败');
          }
        } catch (error) {
          console.error('批量删除失败:', error);
          message.error('批量删除失败，请稍后重试');
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // 表单提交
  const handleFormSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      
      // 处理表单数据
      const formData = {
        ...values,
        // 确保状态有默认值
        status: values.status || 1,
      };
      
      
      let response;
      if (editingRecord) {
        response = await institutionService.update({ ...formData, id: editingRecord.id });
      } else {
        response = await institutionService.create(formData);
      }
      
      
      if (response.code === 200) {
        message.success(editingRecord ? '编辑成功' : '新增成功');
        setIsModalVisible(false);
        form.resetFields();
        loadData();
      } else {
        message.error(response.message || '提交失败');
      }
    } catch (error) {
      console.error('提交失败:', error);
      message.error('提交失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="institution-management">
      {/* 添加状态开关样式 */}
      <style>{statusSwitchStyle}</style>
      
      {/* 统一查询区域 */}
      <QueryArea
        fields={queryFields}
        values={searchParams}
        onValuesChange={handleSearchParamsChange}
        onAdd={handleAdd}
        onBatchDelete={handleBatchDelete}
        batchDeleteDisabled={selectedRowKeys.length === 0}
        selectedCount={selectedRowKeys.length}
        addButtonText="新增"
        batchDeleteButtonText="批量删除"
      />

      {/* 数据表格 */}
      <div className="table-card">
        <Table
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          rowKey="id"
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
          }}
          pagination={{
            current,
            pageSize,
            total,
            ...PAGINATION_CONFIG.simple,
            onChange: (page, size) => {
              setCurrent(page);
              setPageSize(size);
            },
          }}
          scroll={{ x: 710 }}
        />
      </div>

      {/* 新增/编辑模态框 */}
      <Modal
        title={editingRecord ? '编辑机构' : '新增机构'}
        open={isModalVisible}
        onOk={handleFormSubmit}
        onCancel={() => setIsModalVisible(false)}
        width={800}
        confirmLoading={loading}
        okText="确定"
        cancelText="取消"
      >
        <Form
          form={form}
          layout="horizontal"
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 18 }}
          initialValues={{
            status: 1,
          }}
        >
          <Form.Item
            name="name"
            label="机构名称"
            rules={[{ required: true, message: '请输入机构名称' }]}
          >
            <Input placeholder="请输入机构名称" />
          </Form.Item>

          <div style={{ display: 'flex', gap: '16px' }}>
            <Form.Item
              name="type"
              label="机构类型"
              rules={[{ required: true, message: '请选择机构类型' }]}
              style={{ flex: 1 }}
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <DictSelect
                dictType={DICT_TYPES.INSTITUTION_TYPE}
                placeholder="请选择机构类型"
              />
            </Form.Item>

            <Form.Item
              name="status"
              label="状态"
              style={{ flex: 1 }}
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
              valuePropName="checked"
              getValueFromEvent={(checked) => checked ? 1 : 0}
              getValueProps={(value) => ({ checked: value === 1 })}
            >
              <Switch 
                checkedChildren="正常" 
                unCheckedChildren="停用"
                className="status-switch"
                style={{ 
                  backgroundColor: form.getFieldValue('status') === 1 ? '#52c41a' : '#ff4d4f'
                }}
              />
            </Form.Item>
          </div>

          <Form.Item
            name="address"
            label="地址"
          >
            <Input placeholder="请输入详细地址" />
          </Form.Item>

          <div style={{ display: 'flex', gap: '16px' }}>
            <Form.Item
              name="contactPerson"
              label="联系人"
              style={{ flex: 1 }}
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <Input placeholder="请输入联系人姓名" />
            </Form.Item>

            <Form.Item
              name="contactPhone"
              label="联系电话"
              style={{ flex: 1 }}
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <Input placeholder="请输入联系电话" />
            </Form.Item>
          </div>

          <div style={{ display: 'flex', gap: '16px' }}>
            <Form.Item
              name="contactEmail"
              label="邮箱"
              rules={[
                { type: 'email', message: '请输入有效的邮箱地址' }
              ]}
              style={{ flex: 1 }}
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <Input placeholder="请输入邮箱地址" />
            </Form.Item>

            <Form.Item
              name="website"
              label="网站"
              style={{ flex: 1 }}
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <Input placeholder="请输入网站地址" />
            </Form.Item>
          </div>

          <Form.Item
            name="description"
            label="机构描述"
          >
            <TextArea rows={4} placeholder="请输入机构描述" />
          </Form.Item>

          <Form.Item
            name="logo"
            label="机构Logo"
          >
            <Upload
              listType="picture-card"
              maxCount={1}
              showUploadList={{
                showPreviewIcon: false,
                showRemoveIcon: false,
                showDownloadIcon: false,
              }}
              fileList={logoFileList}
              onChange={(info) => {
                setLogoFileList(info.fileList);
                if (info.file.status === 'removed') {
                  form.setFieldsValue({ logo: '' });
                } else if (info.file.status === 'done') {
                  // 确保文件列表正确显示
                  const fileList = info.fileList.slice(-1);
                  if (fileList.length > 0 && fileList[0].url) {
                    form.setFieldsValue({ logo: fileList[0].url });
                  }
                }
              }}
              itemRender={(originNode, file, fileList, actions) => {
                return (
                  <div className="custom-upload-item">
                    {originNode}
                    <div className="upload-actions">
                      <span 
                        className="upload-action-btn"
                        title="查看"
                        onClick={() => {
                          const imageUrl = file.url || file.thumbUrl;
                          if (imageUrl) {
                            setPreviewImage(imageUrl);
                            setPreviewTitle('机构Logo预览');
                            setPreviewVisible(true);
                          }
                        }}
                      >
                        <EyeOutlined />
                      </span>
                      <span 
                        className="upload-action-btn"
                        title="删除"
                        onClick={() => {
                          form.setFieldsValue({ logo: '' });
                          setLogoFileList([]);
                          message.success('Logo已删除');
                        }}
                      >
                        <DeleteOutlined />
                      </span>
                    </div>
                  </div>
                );
              }}
              beforeUpload={(file) => {
                // 检查文件类型
                const isImage = file.type.startsWith('image/');
                if (!isImage) {
                  message.error('只能上传图片文件！');
                  return false;
                }
                
                // 检查文件大小 (2MB，减少Base64数据量)
                const isLt2M = file.size / 1024 / 1024 < 2;
                if (!isLt2M) {
                  message.error('图片大小不能超过 2MB！');
                  return false;
                }
                
                // 压缩图片并转换为Base64
                const compressAndConvert = (file: File) => {
                  const canvas = document.createElement('canvas');
                  const ctx = canvas.getContext('2d');
                  const img = new Image();
                  
                  img.onload = () => {
                    // 计算压缩后的尺寸（最大800x600）
                    const maxWidth = 800;
                    const maxHeight = 600;
                    let { width, height } = img;
                    
                    if (width > height) {
                      if (width > maxWidth) {
                        height = (height * maxWidth) / width;
                        width = maxWidth;
                      }
                    } else {
                      if (height > maxHeight) {
                        width = (width * maxHeight) / height;
                        height = maxHeight;
                      }
                    }
                    
                    canvas.width = width;
                    canvas.height = height;
                    
                    // 绘制压缩后的图片
                    ctx?.drawImage(img, 0, 0, width, height);
                    
                    // 转换为Base64（JPEG格式，质量0.8）
                    const base64 = canvas.toDataURL('image/jpeg', 0.8);
                    form.setFieldsValue({ logo: base64 });
                    message.success('图片上传成功');
                  };
                  
                  img.src = URL.createObjectURL(file);
                };
                
                compressAndConvert(file);
                return false; // 阻止自动上传
              }}
              onRemove={() => {
                form.setFieldsValue({ logo: '' });
                return true;
              }}
            >
              <div>
                <UploadOutlined />
                <div style={{ marginTop: 8 }}>上传Logo</div>
              </div>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>

      {/* Logo预览Modal */}
      <Modal
        open={previewVisible}
        title={previewTitle}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
        width={600}
        styles={{ 
          body: { 
            height: 600, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            padding: '20px'
          }
        }}
      >
        <img 
          alt="机构Logo" 
          style={{ 
            maxWidth: '100%', 
            maxHeight: '100%',
            objectFit: 'contain'
          }} 
          src={previewImage} 
        />
      </Modal>
    </div>
  );
};

export default InstitutionManagement;